import { Stack, Typography } from "@mui/material";
import CustomIconButton from "components/CustomIconButton";
import { useBizTypeTranslation } from "hooks/useBizTypeTranslation";
import { useEffect, useRef, useState } from "react";
import { apiUrl } from "config/settings";
import http from "../../../http";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { setShowLoading, setShowMassage } from "store/sysSlice";
import CustomButton from "components/CustomButton";
import CustomCard from "components/CustomCard";
import {
    AcUnit,
    ExpandLess,
    ExpandMore,
    LocalFireDepartment,
    PowerSettingsNew,
} from "@mui/icons-material";
import useDataManager from "hooks/useDataManager";
import { setMcList } from "store/mcSlice";

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const Thermostat = ({ data, isConnected, checkWeeklyTime, mcList }) => {
    const { bt } = useBizTypeTranslation();
    const { editItem } = useDataManager();
    const dispatch = useDispatch();
    const bizPath = useSelector((state) => state?.biz?.data?.path);

    const holdIntervalRef = useRef(null);
    const lastSentAtRef = useRef({ min: 0, max: 0 });
    const isInteractingRef = useRef(false);

    const absMin = 16;
    const absMax = 30;

    const serverMin = data?.acThermostat?.coolSetPoint ?? 16;
    const serverMax = data?.acThermostat?.heatSetPoint ?? 30;

    const [localMinTemp, setLocalMinTemp] = useState(serverMin);
    const [localMaxTemp, setLocalMaxTemp] = useState(serverMax);

    // טיימרים נפרדים לשליחה
    const sendTimersRef = useRef({ min: null, max: null });

    useEffect(() => {
        if (!isInteractingRef.current && !localMinTemp && !localMaxTemp) {
            setLocalMinTemp(serverMin);
            setLocalMaxTemp(serverMax);
        }
    }, [data]);

    useEffect(() => {
        return () => {
            if (sendTimersRef.current.min) clearTimeout(sendTimersRef.current.min);
            if (sendTimersRef.current.max) clearTimeout(sendTimersRef.current.max);
            if (holdIntervalRef.current) clearInterval(holdIntervalRef.current);
        };
    }, []);

    const canSend = () => {
        if (!data?.isConnected) {
            dispatch(setShowMassage({ massage: "MC_IS_DISCONNECTED", type: "error" }));
            return false;
        }
        if (!isConnected) {
            dispatch(setShowMassage({ massage: "WS_IS_NOT_CONNECTED", type: "info" }));
            return false;
        }
        return true;
    };

    // ✅ עכשיו שולח גם command + commandValue
    const patchTemps = async ({ coolSetPoint, heatSetPoint, command, commandValue }) => {
        if (!canSend()) return;

        const payload = {
            turnOnDate: data?.turnOnDate,
            turnOffDate: data?.turnOffDate,
            coolSetPoint: coolSetPoint ?? data?.acThermostat?.coolSetPoint,
            heatSetPoint: heatSetPoint ?? data?.acThermostat?.heatSetPoint,
            command,
            commandValue,
        };

        if ("vibrate" in navigator) navigator.vibrate(50);

        dispatch(setShowLoading(true));
        try {
            await http.patch(`${apiUrl}/mc/sendCommand/${bizPath}/${data?.guid}`, payload);
            const newItem = {
                ...data,
                acThermostat: {
                    coolSetPoint: payload.coolSetPoint,
                    heatSetPoint: payload.heatSetPoint,
                    modeActive: data?.acThermostat?.modeActive,
                }
            }
            const newList = editItem(newItem, mcList);
            dispatch(setMcList(newList))

        }
        catch (e) {
        }
        finally {
            dispatch(setShowLoading(false));
        }
    };

    const scheduleSend = (key, nextMin, nextMax) => {
        setLocalMinTemp(nextMin);
        setLocalMaxTemp(nextMax);

        if (sendTimersRef.current[key]) clearTimeout(sendTimersRef.current[key]);

        sendTimersRef.current[key] = setTimeout(() => {
            const now = Date.now();
            const last = lastSentAtRef.current[key] || 0;

            const doSend = () => {
                lastSentAtRef.current[key] = Date.now();

                const command = key === "min" ? "coolSetPoint" : "heatSetPoint";
                const commandValue = key === "min" ? nextMin : nextMax;

                patchTemps({
                    coolSetPoint: nextMin,
                    heatSetPoint: nextMax,
                    command,
                    commandValue,
                });

                sendTimersRef.current[key] = null;
            };

            if (now - last < 400) {
                sendTimersRef.current[key] = setTimeout(doSend, 400);
            } else {
                doSend();
            }
        }, 700);
    };

    const step = (which, direction) => {
        const curMin = typeof localMinTemp === "number" ? localMinTemp : serverMin;
        const curMax = typeof localMaxTemp === "number" ? localMaxTemp : serverMax;

        if (which === "min") {
            let nextMin = direction === "up" ? curMin + 1 : curMin - 1;
            nextMin = clamp(nextMin, absMin, absMax);

            if (nextMin > curMax) nextMin = curMax;

            scheduleSend("min", nextMin, curMax);
            return;
        }

        if (which === "max") {
            let nextMax = direction === "up" ? curMax + 1 : curMax - 1;
            nextMax = clamp(nextMax, absMin, absMax);

            if (nextMax < curMin) nextMax = curMin;

            scheduleSend("max", curMin, nextMax);
        }
    };

    const handlePressStart = (which, direction) => {
        isInteractingRef.current = true;
        step(which, direction);

        if (holdIntervalRef.current) clearInterval(holdIntervalRef.current);
        holdIntervalRef.current = setInterval(() => step(which, direction), 200);
    };

    const handlePressEnd = () => {
        isInteractingRef.current = false;
        if (holdIntervalRef.current) {
            clearInterval(holdIntervalRef.current);
            holdIntervalRef.current = null;
        }
    };

    const powerHandle = async (action, overrideSchedule = false) => {
        if (sendTimersRef.current.min) clearTimeout(sendTimersRef.current.min);
        if (sendTimersRef.current.max) clearTimeout(sendTimersRef.current.max);
        sendTimersRef.current.min = null;
        sendTimersRef.current.max = null;

        if (holdIntervalRef.current) clearInterval(holdIntervalRef.current);
        holdIntervalRef.current = null;

        if (!canSend()) return;

        const payload = {
            overrideSchedule,
            turnOnDate: data?.turnOnDate,
            turnOffDate: data?.turnOffDate,
            coolSetPoint: localMinTemp,
            heatSetPoint: localMaxTemp,
            command: action,
            commandValue: action, // אם אתה רוצה מספר/משהו אחר פה תגיד
        };

        if (action === "on") {
            payload.turnOnDate = moment();
            payload.turnOffDate = null;
        } else {
            payload.turnOffDate = moment();
            payload.turnOnDate = null;
        }

        if ("vibrate" in navigator) navigator.vibrate(100);

        dispatch(setShowLoading(true));
        try {
            await http.patch(`${apiUrl}/mc/sendCommand/${bizPath}/${data?.guid}`, payload);
        } catch (e) {
        } finally {
            dispatch(setShowLoading(false));
        }
    };

    const isAnySending =
        !!sendTimersRef.current.min ||
        !!sendTimersRef.current.max ||
        !!holdIntervalRef.current;

    return (
        <Stack justifyContent="center" alignItems="center" spacing={3}>
            <CustomButton
                disabled={isAnySending}
                startIcon={
                    <PowerSettingsNew
                        sx={{ color: data?.turnOnDate && data?.isConnected ? "success.text" : "error.text" }}
                    />
                }
                title={data?.turnOnDate && data?.isConnected ? bt("ON_MC") : bt("OFF_MC")}
                fullWidth
                height={40}
                color={data?.turnOnDate && data?.isConnected ? "success.main" : "error.main"}
                textColor={data?.turnOnDate && data?.isConnected ? "success.text" : "error.text"}
                onClick={() => checkWeeklyTime({ callbackFunc: ({ overrideSchedule = false } = {}) => powerHandle(data?.turnOnDate ? "off" : "on", overrideSchedule) })}
            />

            <Stack spacing={3} width="100%">
                {/* MAX */}
                <CustomCard bgcolor="error.main" padding={2} pb={2} spacing={3}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <LocalFireDepartment sx={{ color: "error.text" }} />
                        <Typography variant="h6">{bt("MAX_SETPOINT")}</Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={3}>
                        <CustomIconButton
                            disabled={localMaxTemp >= absMax}
                            icon={<ExpandLess sx={{ color: "bg.main" }} fontSize="large" />}
                            onClick={() => step("max", "up")}
                            onMouseDown={() => handlePressStart("max", "up")}
                            onMouseUp={handlePressEnd}
                            onMouseLeave={handlePressEnd}
                            onTouchStart={() => handlePressStart("max", "up")}
                            onTouchEnd={handlePressEnd}
                        />

                        <Typography variant="h3" fontWeight="bold">
                            {`${localMaxTemp ?? 0}°`}
                        </Typography>

                        <CustomIconButton
                            disabled={localMaxTemp <= localMinTemp}
                            icon={<ExpandMore sx={{ color: "bg.main" }} fontSize="large" />}
                            onClick={() => step("max", "down")}
                            onMouseDown={() => handlePressStart("max", "down")}
                            onMouseUp={handlePressEnd}
                            onMouseLeave={handlePressEnd}
                            onTouchStart={() => handlePressStart("max", "down")}
                            onTouchEnd={handlePressEnd}
                        />
                    </Stack>
                </CustomCard>

                {/* MIN */}
                <CustomCard bgcolor="info.text" padding={2} pb={2} spacing={3}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <AcUnit sx={{ color: "info.main" }} />
                        <Typography variant="h6">{bt("MIN_SETPOINT")}</Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={3}>
                        <CustomIconButton
                            disabled={localMinTemp >= localMaxTemp}
                            icon={<ExpandLess sx={{ color: "bg.main" }} fontSize="large" />}
                            onClick={() => step("min", "up")}
                            onMouseDown={() => handlePressStart("min", "up")}
                            onMouseUp={handlePressEnd}
                            onMouseLeave={handlePressEnd}
                            onTouchStart={() => handlePressStart("min", "up")}
                            onTouchEnd={handlePressEnd}
                        />

                        <Typography variant="h3" fontWeight="bold">
                            {`${localMinTemp ?? 0}°`}
                        </Typography>

                        <CustomIconButton
                            disabled={localMinTemp <= absMin}
                            icon={<ExpandMore sx={{ color: "bg.main" }} fontSize="large" />}
                            onClick={() => step("min", "down")}
                            onMouseDown={() => handlePressStart("min", "down")}
                            onMouseUp={handlePressEnd}
                            onMouseLeave={handlePressEnd}
                            onTouchStart={() => handlePressStart("min", "down")}
                            onTouchEnd={handlePressEnd}
                        />
                    </Stack>
                </CustomCard>
            </Stack>
        </Stack>
    );
};

export default Thermostat;
